import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, Loader2, Plus, User, Phone, Signal, Wallet, MapPin, Users } from "lucide-react";
import { api } from "@/lib/api";
import { sourceLabel } from "@/lib/format";

export default function Subscribers() {
  const [subs, setSubs] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", phone: "", tariff: "", balance: "", address: "" });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async (query) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/subscribers", { params: query ? { q: query } : {} });
      setSubs(data.subscribers);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(""); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => load(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q, load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim() && !form.phone.trim()) {
      toast.error("Вкажіть ім'я або телефон");
      return;
    }
    setCreating(true);
    try {
      await api.post("/admin/subscribers", {
        name: form.name.trim() || null,
        phone: form.phone.trim() || null,
        tariff: form.tariff.trim() || null,
        balance: form.balance === "" ? null : Number(form.balance),
        address: form.address.trim() || null,
      });
      setForm({ name: "", phone: "", tariff: "", balance: "", address: "" });
      toast.success("Абонента додано");
      load(q.trim());
    } catch {
      toast.error("Не вдалося додати абонента");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6">
        <div>
          <h1 className="font-head text-2xl md:text-3xl font-bold tracking-tight">Абоненти</h1>
          <p className="text-slate-500 text-sm mt-1">База клієнтів провайдера</p>
        </div>

        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="subscriber-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Пошук за ім'ям, телефоном, адресою, тарифом..."
            className="w-full border border-slate-200 rounded-[10px] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F76BC]/20 focus:border-[#0F76BC] transition-[border-color,box-shadow] bg-white"
          />
        </div>

        <form onSubmit={create} data-testid="add-subscriber-form" className="bg-white border border-slate-200 rounded-[12px] p-4 shadow-sm grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
          <input data-testid="new-sub-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ім'я" className="border border-slate-200 rounded-[10px] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F76BC]/20 focus:border-[#0F76BC]" />
          <input data-testid="new-sub-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Телефон" className="border border-slate-200 rounded-[10px] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F76BC]/20 focus:border-[#0F76BC]" />
          <input data-testid="new-sub-tariff" value={form.tariff} onChange={(e) => setForm({ ...form, tariff: e.target.value })} placeholder="Тариф" className="border border-slate-200 rounded-[10px] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F76BC]/20 focus:border-[#0F76BC]" />
          <input data-testid="new-sub-balance" type="number" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} placeholder="Баланс" className="border border-slate-200 rounded-[10px] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F76BC]/20 focus:border-[#0F76BC]" />
          <input data-testid="new-sub-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Адреса" className="border border-slate-200 rounded-[10px] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F76BC]/20 focus:border-[#0F76BC]" />
          <button data-testid="add-subscriber-btn" type="submit" disabled={creating} className="bg-[#0F76BC] hover:bg-[#0c5f96] text-white px-4 py-2.5 rounded-[10px] font-semibold text-sm flex items-center justify-center gap-2 transition-[background-color,transform] active:scale-95 disabled:opacity-60">
            {creating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Додати
          </button>
        </form>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-300" size={28} /></div>
        ) : subs.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <Users size={32} className="mx-auto mb-2 opacity-40" />
            Абонентів не знайдено
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="subscribers-list">
            {subs.map((s) => (
              <div key={s.id} data-testid={`subscriber-${s.id}`} className="bg-white border border-slate-200 rounded-[12px] p-4 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-full bg-[#0F76BC]/10 text-[#0F76BC] flex items-center justify-center font-bold uppercase">
                    {(s.name || s.phone || "?")[0]}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-800 truncate">{s.name || "Без імені"}</div>
                    {s.source && <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{sourceLabel(s.source)}</span>}
                  </div>
                </div>
                <div className="space-y-1.5 text-sm">
                  {s.phone && <div className="flex items-center gap-2 text-slate-600"><Phone size={13} className="text-slate-400" /> {s.phone}</div>}
                  {s.tariff && <div className="flex items-center gap-2 text-slate-600"><Signal size={13} className="text-slate-400" /> {s.tariff}</div>}
                  {s.balance != null && <div className="flex items-center gap-2 text-slate-600"><Wallet size={13} className="text-slate-400" /> {s.balance} грн</div>}
                  {s.address && <div className="flex items-center gap-2 text-slate-600"><MapPin size={13} className="text-slate-400" /> {s.address}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
